import { domToReact } from 'html-react-parser';
import * as React from 'react';
import { FormattedMessage, IntlShape, PrimitiveType } from 'react-intl';
import { Link } from 'react-router-dom';

import { getIsInternalLink } from '../../components/Learn/LearnBlocks/BlockText';
import { localeData } from '../../locales/data';
import { StringTMap } from '../../types';
import { APP_EMAIL } from '../../config';
import {
  ANNOUNCEMENTS_PAGE_PATH,
  COMMUNITY_TOPICS_URL,
  FAQ_URL,
  LEARN_ALPHA_EXAMPLES_PAGE_PATH,
  LEARN_ALPHA_SUBMISSION_PAGE_PATH,
  LEARN_PAGE_PATH,
  LEARN_SEMI_OS_OS_PAGE_PATH,
  LEARN_STARTER_PACK_PATH,
  LEARN_TUTORIALS_PAGE_PATH,
  LEARN_TUTORIAL_SIMULATION_RESULTS,
  LEARN_VIDEO_COURSES_PAGE_PATH,
  NOTIFICATIONS_PAGE_PATH,
  PROFILE_ACCOUNT_PAGE_PATH,
  PROFILE_ACCOUNT_WORKDAY_PAGE_PATH,
  PROFILE_ACHIEVEMENTS_PAGE_PATH,
  PROFILE_PERFORMANCE_PAGE_PATH,
  SUPPORT_URL,
  CONSULTANT_FAQ_LINK,
  COMPETITION_IQC_GUIDELINES,
  LEARN_TEST_PERIOD_PATH,
  LEARN_OPERATORS_PAGE_PATH,
  GENIUS_URL,
} from '../constants';
import analytics from '../services/analytics/analytics';
import { ElementType } from 'react';
import {
  DATA_FIELD_LIST_PATH,
  DATASET_LIST_PATH,
  getDataSearchResultPath,
  getDatasetsPath,
} from '../../containers/DataExplorer/dataExplorerRouterPaths';
import { PATH_ANALYTICS_KEYS } from '../../containers/DataExplorer/Filters/dataExplorerAnalytics';

export const internalLinkReplace = (domNode: any) => {
  if (domNode.name === 'a' && domNode.attribs.href && getIsInternalLink(domNode.attribs.href)) {
    return <Link to={domNode.attribs.href}>{domToReact(domNode.children)}</Link>;
  }
};

export const htmlTagRendererValues = {
  nbsp: <>&nbsp;</>,
  br: <br />,
  p: (children: React.ReactNode[]) => <p>{children}</p>,
  b: (children: React.ReactNode[]) => <b>{children}</b>,
  u: (children: React.ReactNode[]) => <u>{children}</u>,
  ol: (children: React.ReactNode[]) => <ol>{children}</ol>,
  li: (children: React.ReactNode[]) => <li>{children}</li>,
  strong: (children: React.ReactNode[]) => <strong>{children}</strong>,
};

interface MakeLinkRendererParams {
  href: string;
  title?: string;
  isOpenInNewTab?: boolean;
  analyticsOptions?: { event: string; event_label?: string; isHandlerDisabled?: boolean };
}

export const EVENT_ATTRIBUTE = 'brain-data-event';
export const EVENT_LABEL_ATTRIBUTE = 'brain-data-event-label';

export const makeLinkRenderer = ({ href, isOpenInNewTab, analyticsOptions, title }: MakeLinkRendererParams) => {
  const target = isOpenInNewTab ? '_blank' : undefined;
  const shouldUseLinkComponent = getIsInternalLink(href) && target === undefined;
  const onClick = analyticsOptions
    ? () => {
        analytics.trackCustomEvent(analyticsOptions.event, { event_label: analyticsOptions?.event_label });
      }
    : undefined;

  // Used in walkthrough as IntroJS remove handlers, so click does not send any event
  // See corresponding code in: EditorIntroIntl.handleClickExternalLink
  const additionalAttributes = analyticsOptions?.isHandlerDisabled
    ? {
        [EVENT_ATTRIBUTE]: analyticsOptions.event,
        [EVENT_LABEL_ATTRIBUTE]: analyticsOptions.event_label,
      }
    : {};

  return (text: string) => {
    if (shouldUseLinkComponent) {
      return (
        <span {...additionalAttributes}>
          <Link onClick={onClick} to={href} title={title}>
            {text}
          </Link>
        </span>
      );
    }
    return (
      <a onClick={onClick} href={href} target={target} title={title} {...additionalAttributes}>
        {text}
      </a>
    );
  };
};

export const linkRendererValues = {
  linkMailToSupport: makeLinkRenderer({ href: `mailto:${APP_EMAIL}` }),

  linkProfileAccountBasicDetails: makeLinkRenderer({ href: PROFILE_ACCOUNT_PAGE_PATH }),
  linkProfileAccountWorkday: makeLinkRenderer({ href: PROFILE_ACCOUNT_WORKDAY_PAGE_PATH }),

  // Links open in new tab
  linkInNewTabSupport: makeLinkRenderer({ href: SUPPORT_URL, isOpenInNewTab: true }),
  linkInNewTabWorldQuantBrain: makeLinkRenderer({ href: 'https://worldquant.com/brain', isOpenInNewTab: true }),

  linkInNewTabMessagesAnnoucementsAll: makeLinkRenderer({ href: ANNOUNCEMENTS_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabMessagesNotificationsAll: makeLinkRenderer({ href: NOTIFICATIONS_PAGE_PATH, isOpenInNewTab: true }),

  linkInNewTabProfileAchievements: makeLinkRenderer({ href: PROFILE_ACHIEVEMENTS_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabProfileAccountBasicDetails: makeLinkRenderer({ href: PROFILE_ACCOUNT_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabProfilePerformance: makeLinkRenderer({ href: PROFILE_PERFORMANCE_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabReferralAgreement: makeLinkRenderer({ href: '/agreements/referral-agreement', isOpenInNewTab: true }),

  linkInNewTabLearn: makeLinkRenderer({ href: LEARN_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabLearnCourses: makeLinkRenderer({ href: LEARN_VIDEO_COURSES_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabLearnDocumentation: makeLinkRenderer({ href: LEARN_TUTORIALS_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabLearnAlphaExamples: makeLinkRenderer({
    href: LEARN_ALPHA_EXAMPLES_PAGE_PATH,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_link_walkthrough',
      event_label: 'alpha_examples',
      isHandlerDisabled: true,
    },
  }),
  linkInNewTabLearnSemiOsOs: makeLinkRenderer({
    href: LEARN_SEMI_OS_OS_PAGE_PATH,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_link_walkthrough',
      event_label: 'out_of_sample',
      isHandlerDisabled: true,
    },
  }),
  linkInNewTabLearnSimulationResults: makeLinkRenderer({
    href: LEARN_TUTORIAL_SIMULATION_RESULTS,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_link_walkthrough',
      event_label: 'is_summary',
      isHandlerDisabled: true,
    },
  }),
  linkInNewTabLearnTestPeriod: makeLinkRenderer({
    href: LEARN_TEST_PERIOD_PATH,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_link_walkthrough',
      event_label: 'test_period',
      isHandlerDisabled: true,
    },
  }),
  linkInNewTabLearnAlphaSubmission: makeLinkRenderer({
    href: LEARN_ALPHA_SUBMISSION_PAGE_PATH,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_link_walkthrough',
      event_label: 'alpha_submission',
      isHandlerDisabled: true,
    },
  }),
  linkInNewTabLearnStarterPack: makeLinkRenderer({ href: LEARN_STARTER_PACK_PATH, isOpenInNewTab: true }),
  linkInNewTabLearnDataAndOperators: makeLinkRenderer({ href: LEARN_OPERATORS_PAGE_PATH, isOpenInNewTab: true }),
  linkInNewTabLearnConsultantDatasets: makeLinkRenderer({
    href: `${LEARN_OPERATORS_PAGE_PATH}/consultant-datasets`,
    isOpenInNewTab: true,
  }),
  linkInNewTabLearnMultiAlphaSimulation: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/create-alphas/multi-alpha-simulation`,
    isOpenInNewTab: true,
  }),
  linkInNewTabLearnConsultantFAQs: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/consultant-information/consultant-faqs`,
    isOpenInNewTab: true,
  }),
  linkInNewTabLearnConsultantDosAndDonts: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/consultant-information/consultant-dos-and-donts`,
    isOpenInNewTab: true,
  }),
  linkInNewTabLearnConsultantVisualizationTool: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/consultant-information/visualization-tool`,
    isOpenInNewTab: true,
  }),
  linkInNewTabLearnBrainGenius: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/consultant-information/brain-genius#what-happens-if-more-consultants-qualify-for-a-level-than-there-are-available-spots`,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'streak_freeze_unlock_modal_click_brain_genius',
    },
  }),
  linkInNewTabGeniusDashboard: makeLinkRenderer({ href: GENIUS_URL, isOpenInNewTab: true }),

  linkInNewTabHelpCenterCommunityTopics: makeLinkRenderer({ href: COMMUNITY_TOPICS_URL, isOpenInNewTab: true }),
  linkInNewtabHelpCenterNewRequest: makeLinkRenderer({ href: `${FAQ_URL}/requests/new`, isOpenInNewTab: true }),
  linkInNewTabHelpCenterFAQs: makeLinkRenderer({ href: `${FAQ_URL}/categories/4413011872791-FAQ`, isOpenInNewTab: true }),

  linkInNewTabGoToWebinarUserVBC1: makeLinkRenderer({
    href: 'https://attendee.gotowebinar.com/register/4199581764001057549',
    isOpenInNewTab: true,
  }),
  linkInNewTabGoToWebinarUserVBC2: makeLinkRenderer({
    href: 'https://attendee.gotowebinar.com/register/7487231482214091277',
    isOpenInNewTab: true,
  }),
  linkInNewTabGoToWebinarConsultantVBC1: makeLinkRenderer({
    href: 'https://attendee.gotowebinar.com/register/79743580561461005',
    isOpenInNewTab: true,
  }),
  linkInNewTabGoToWebinarConsultantVBC2: makeLinkRenderer({
    href: 'https://attendee.gotowebinar.com/register/6073173766987139085',
    isOpenInNewTab: true,
  }),
  linkIQCWebinarEnglish: makeLinkRenderer({
    href: 'https://worldquant.zoom.us/webinar/register/1117756364713/WN_PHTjN28aTeKBbPCDMUoQfA#/registration',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_english',
    },
  }),
  linkIQCWebinarEnglishSGMY: makeLinkRenderer({
    href: 'https://worldquant.zoom.us/webinar/register/WN_LfXA31v6SoySQdgkEVTwMQ#/registration',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_english_sgmy',
    },
  }),
  linkIQCWebinarSimplifiedChinese: makeLinkRenderer({
    href: 'https://www.surveymonkey.com/r/7ZMNLY8',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_simplified_chinese',
    },
  }),
  linkIQCWebinarTraditionalChinese: makeLinkRenderer({
    href: 'https://worldquant.zoom.us/webinar/register/4617708041936/WN_y2OUVcjvROKFMKZrNw_WBA#/registration',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_traditional_chinese',
    },
  }),
  linkIQCWebinarKorean: makeLinkRenderer({
    href: 'https://qr.wqbrain.com/IQC2026_KRwebinar',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_korean',
    },
  }),
  linkIQCWebinarVietnamese: makeLinkRenderer({
    href: 'https://worldquant.zoom.us/webinar/register/5817761374789/WN_aSXvsy45Q-WZkYfIQlszAQ',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_vietnamese',
    },
  }),
  linkIQCWebinarThai: makeLinkRenderer({
    href: 'https://worldquant.zoom.us/webinar/register/4417736337051/WN_Jjs37laoSgScClU08GMcJw#/registration',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'webinar_thai',
    },
  }),
  linkIQCWorldquant: makeLinkRenderer({
    href: 'https://www.worldquant.com/brain/iqc',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'wq_iqc_landing',
    },
  }),
  linkIQCGuidelines: makeLinkRenderer({
    href: COMPETITION_IQC_GUIDELINES,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'click_iqc_page',
      event_label: 'iqc_guidelines',
    },
  }),
  linkToAllDataFields: makeLinkRenderer({
    href: getDataSearchResultPath({ tab: 'data-fields' }),
    analyticsOptions: {
      event: 'de_click_all_fields',
      event_label: PATH_ANALYTICS_KEYS[DATA_FIELD_LIST_PATH],
    },
  }),
  linkToAllDatasets: makeLinkRenderer({
    href: getDatasetsPath(),
    analyticsOptions: {
      event: 'de_click_all_datasets',
      event_label: PATH_ANALYTICS_KEYS[DATASET_LIST_PATH],
    },
  }),
  linkReferralProgram: makeLinkRenderer({
    href: '/referral',
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'cl_click_referral',
      event_label: PATH_ANALYTICS_KEYS[DATASET_LIST_PATH],
    },
  }),
  linkThreshold: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/interpret-results/alpha-submission`,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'cl_click_referral',
      event_label: PATH_ANALYTICS_KEYS[DATASET_LIST_PATH],
    },
  }),
  linkConsultantProcess: makeLinkRenderer({
    href: CONSULTANT_FAQ_LINK,
    isOpenInNewTab: true,
    analyticsOptions: {
      event: 'cl_click_process',
      event_label: PATH_ANALYTICS_KEYS[DATASET_LIST_PATH],
    },
  }),
  linkToBetterFitness: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/20251386376471`,
    isOpenInNewTab: true,
    title: 'How to increase fitness of alphas',
    analyticsOptions: {
      event: 'sim_click_better_fitness',
    },
  }),
  linkToBetterTurnover: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/20251419309719`,
    isOpenInNewTab: true,
    title: 'How to improve Turnover?',
    analyticsOptions: {
      event: 'sim_click_better_turnover',
    },
  }),
  linkToBetterCorrelation: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/20251385275671`,
    isOpenInNewTab: true,
    title: 'How do you reduce correlation of a good Alpha?',
    analyticsOptions: {
      event: 'sim_click_better_correlation',
    },
  }),
  linkToBetterSharpe: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/20251383456663`,
    isOpenInNewTab: true,
    title: 'How to improve Sharpe?',
    analyticsOptions: {
      event: 'sim_click_better_sharpe',
    },
  }),
  linkToBetterReturn: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/20251364149655`,
    isOpenInNewTab: true,
    title: 'How to improve returns?',
    analyticsOptions: {
      event: 'sim_click_better_return',
    },
  }),
  linkToBetterWeight: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/19248385997719`,
    isOpenInNewTab: true,
    title: 'Weight Coverage common issues and advice',
    analyticsOptions: {
      event: 'sim_click_better_weight',
    },
  }),
  linkToBetterLadder: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/6726865162903`,
    isOpenInNewTab: true,
    title: 'Ideas to clear the submission test - IS ladder Sharpe is below cutoff for ladder year',
    analyticsOptions: {
      event: 'sim_click_better_ladder',
    },
  }),
  linkToBetterSubUniverseSharpe: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/6568644868375`,
    isOpenInNewTab: true,
    title: 'How do I resolve this error: “Sub-universe Sharpe NaN is not above cutoff…”?',
    analyticsOptions: {
      event: 'sim_click_better_sub-universe-sharpe',
    },
  }),
  linkToAfterCostSharpe: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/25960264611351-What-s-After-cost-Sharpe-ratio `,
    isOpenInNewTab: true,
    title: "What's After cost Sharpe ratio?",
    analyticsOptions: {
      event: 'sim_click_after_cost_sharpe',
    },
  }),
  linkToDailyOsmosisRank: makeLinkRenderer({
    href: `${LEARN_TUTORIALS_PAGE_PATH}/consultant-information/osmosis-allocation-guide-consultants`,
    isOpenInNewTab: true,
    title: 'Osmosis Allocation Guide for Consultants',
    analyticsOptions: {
      event: 'sim_click_daily_osmosis_rank',
    },
  }),
  linkPowerPoolTheme: makeLinkRenderer({
    href: `${SUPPORT_URL}/hc/en-us/articles/38927747787031-Current-month-Power-Pool-Themes`,
    isOpenInNewTab: true,
    title: 'Current month Power Pool Themes',
    analyticsOptions: {
      event: 'sim_click_power_pool_theme',
    },
  }),
};

export function formatMessageHelper(intl: IntlShape, key: string, values: StringTMap<string | number | boolean> = {}) {
  return intl.formatMessage({ id: key, defaultMessage: localeData.en[key] }, values);
}

interface FormatHTMLRawMessageHelperOptions {
  key: string;
  defaultMessage?: string;
  values?: Record<string, PrimitiveType | React.ReactNode>;
}

export function formatHTMLRawMessageHelper(intl: IntlShape, options: FormatHTMLRawMessageHelperOptions) {
  return intl.formatMessage<React.ReactNode>(
    { id: options.key, defaultMessage: options.defaultMessage ?? localeData.en[options.key] },
    // @ts-expect-error react-intl values record type too strict
    { ...options.values, ...linkRendererValues, ...htmlTagRendererValues }
  );
}

export function formatHTMLMessageHelper(intl: IntlShape, key: string, values: Record<string, PrimitiveType | React.ReactNode> = {}) {
  return formatHTMLRawMessageHelper(intl, { key, values });
}

interface FormattedMessageHelperProps {
  id: string;
  values?: any;
  noTag?: boolean;
  tagName?: string;
  defaultMessage?: string;
  ignoreTag?: boolean;
}

export const FormattedMessageHelper = ({
  id,
  values,
  tagName = 'span',
  defaultMessage = localeData.en[id],
  ignoreTag = false,
  noTag = false,
}: FormattedMessageHelperProps) => (
  <FormattedMessage
    tagName={noTag ? undefined : (tagName as ElementType)}
    id={id}
    defaultMessage={defaultMessage}
    values={{
      ...values,
      ...linkRendererValues,
      ...htmlTagRendererValues,
    }}
    ignoreTag={ignoreTag}
  />
);
